import { Injectable } from '@angular/core';
import * as moment from 'moment';

import { AppSessionService } from '@shared/common/session/app-session.service';

/** Anything a task/event date can arrive as: raw ISO from the proxy, a Date, or an already-parsed moment. */
export type CompanyDateInput = string | Date | moment.Moment | null | undefined;

/**
 * The ONE "today" every My Day surface buckets against: **the company's calendar date.**
 *
 * WHY THIS EXISTS
 * My Day is a single surface with shared day headers, so it needs a single day boundary — the
 * FIRM's, not the laptop's. Before this service, `filterTasksByMode`, `updateCounts`,
 * `updateNewCountsPerPill`, the date-column labels, the overdue styling and both mobile bucketing
 * passes each called bare `moment()`, i.e. the machine clock, which is neither the company tz nor
 * the user's. On vf-app-dev (company UTC+03, dev machine UTC-04) that is a 7-hour disagreement, so
 * for ~3 hours every night the server said "overdue" and the grid said "due today".
 *
 * WHAT "TODAY" MEANS HERE — COMPANY CALENDAR MIDNIGHT
 * The day flips at 00:00 in the company timezone. It is deliberately NOT the shift-derived
 * `companyTzWorkDate` (`FN_CompanyTz_User_WorkDate` -> `FN_User_Shift_DateTime`), even though half
 * the SQL layer still computes `IsDueDatePassed` against that: **shifts and check-in/out are being
 * removed** (see CHECKIN_ELIMINATION_PLAN.md), so anchoring the whole surface to a shift concept
 * would mean re-anchoring it again later. Product decision, 2026-08-10.
 *
 * ⚠️ TRANSITIONAL: until shifts are gone, a NIGHT-SHIFT user's `companyTzWorkDate` can differ from
 * the company calendar date, so My Day's headers and the server's task carry-forward can disagree
 * about which day it is for that user. Day-shift users — everyone, once shifts are removed — are
 * unaffected because the two values coincide. Accepted; see TIMEZONE_LOGIC_REGRESSION_PLAN.md §2 (D7).
 *
 * ALWAYS-FRESH BY CONSTRUCTION
 * The primary source is a LIVE computation (`moment()` shifted to the company offset), not a
 * session snapshot, so a tab left open across midnight re-buckets correctly on the next recompute
 * without waiting for a session refresh. Nothing here is memoized — a cached "today" is the bug
 * this service exists to kill.
 *
 * See TIMEZONE_LOGIC_REGRESSION_PLAN.md (Phase 1).
 */
@Injectable()
export class CompanyNowService {

    constructor(private _appSession: AppSessionService) { }

    /**
     * The company's current calendar date as `YYYY-MM-DD` — the day boundary for ALL My Day
     * bucketing, counts, labels and overdue styling.
     */
    today(): string {
        return this.nowMoment().format('YYYY-MM-DD');
    }

    /** The company calendar date offset by `days`, as `YYYY-MM-DD`. Negative values go backwards. */
    todayPlus(days: number): string {
        return this.nowMoment().add(days, 'days').format('YYYY-MM-DD');
    }

    /**
     * "Now" pinned to the company's UTC offset. Callers that only need a day comparison should
     * prefer `today()` / `dayKey()` — string compare can't drift.
     */
    nowMoment(): moment.Moment {
        const user = this._appSession.user;
        if (user) {
            // 1. LIVE company wall clock. Preferred because it stays correct on a long-open tab,
            //    unlike the snapshot below.
            //    Caveat: companyTimeZoneOffset is the offset as at session load, so on a DST
            //    changeover day it can be an hour stale — which only matters within an hour of
            //    midnight on that one day. Same limitation DayRolloverService already lives with.
            const offset = CompanyNowService.normalizeOffset(user.companyTimeZoneOffset);
            if (offset) {
                return moment().utcOffset(offset);
            }
            // 2. Server's rendering of company "now" — correct when issued, but a SNAPSHOT taken at
            //    session load, so it goes stale as the tab stays open. Only used when no offset is
            //    available to compute from.
            const companyClock = CompanyNowService.parse(user.currentTimeAsCompanyTz);
            if (companyClock) {
                return companyClock;
            }
        }
        // 3. Machine clock. Reached only pre-session (login) or when the tenant has no timezone
        //    configured at all — the exact case this service is replacing, so it stays a last
        //    resort and never a silent default for a logged-in user on a configured tenant.
        return moment();
    }

    /** Company-tz start of the current week (Sunday — matching moment's `en` locale, as before). */
    startOfWeek(): moment.Moment {
        return this.nowMoment().startOf('week');
    }

    /** Company-tz end of the current week (Saturday). */
    endOfWeek(): moment.Moment {
        return this.nowMoment().endOf('week');
    }

    /**
     * Normalize a task date to its `YYYY-MM-DD` calendar day, or null when absent.
     *
     * ⚠️ Uses `parseZone`, NOT `moment()`. Task start/due are date-only intents stored as a
     * midnight instant; plain `moment('2026-08-10T00:00:00Z')` re-renders that in the BROWSER's
     * zone, which west of UTC yields 2026-08-09 — an off-by-one on the task's own date, on top of
     * the wrong "today" this service fixes. `parseZone` keeps the wall clock exactly as sent, so
     * the day is whatever the writer meant regardless of how the API serializes it.
     *
     * (Measured: the API currently sends naive `2026-08-10T00:00:00` with no `Z`, so this is
     * hardening rather than a live fix on the main read path — but the SignalR payloads are not
     * guaranteed to match.)
     */
    dayKey(value: CompanyDateInput): string | null {
        const parsed = CompanyNowService.parse(value);
        return parsed ? parsed.format('YYYY-MM-DD') : null;
    }

    /**
     * The company calendar date an INSTANT falls on, as `YYYY-MM-DD`, or null when absent or unparseable.
     *
     * ⚠️ Not `dayKey()`. That one keeps a date-only intent's wall clock (a task's start/due), which is right
     * for a date the firm wrote down. This is for a point in time (an email received, a follow-up armed),
     * which has to be moved onto the firm's clock before it has a calendar date at all. A string with no
     * offset is read as UTC, which is how the API serializes these timestamps (`LastInboundAtUtc`,
     * `ArmedAtUtc`, …).
     *
     * Uses the company's CURRENT offset, the only one the session carries, so a timestamp from before a DST
     * change that sits within an hour of midnight can land on the neighbouring day.
     */
    instantDayKey(value: CompanyDateInput): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const instant = moment.isMoment(value) ? value.clone() : moment.utc(value);
        return instant.isValid() ? instant.utcOffset(this.nowMoment().utcOffset()).format('YYYY-MM-DD') : null;
    }

    /**
     * Whole company-calendar days from `day` (a `YYYY-MM-DD` key from `dayKey()` or `instantDayKey()`) back to
     * today: 0 = today, 1 = yesterday. Calendar days, not elapsed 24h blocks: `moment().diff(x, 'days')` floors
     * the DURATION, so a reply at 3 PM three days back reads "2 days ago" until 3 PM and "3" after it, a number
     * that disagrees with the date on the email. An absent day, or one after today, counts as 0.
     */
    calendarDaysAgo(day: string | null): number {
        if (!day) {
            return 0;
        }
        const then = moment.utc(day, 'YYYY-MM-DD', true);
        if (!then.isValid()) {
            return 0;
        }
        const days = moment.utc(this.today(), 'YYYY-MM-DD', true).diff(then, 'days');
        return days > 0 ? days : 0;
    }

    /** True when `dueDate` falls strictly before today. Absent due date is never overdue. */
    isOverdue(dueDate: CompanyDateInput): boolean {
        const due = this.dayKey(dueDate);
        return due !== null && due < this.today();
    }

    /** True when the date lands on the company's today. */
    isToday(value: CompanyDateInput): boolean {
        const day = this.dayKey(value);
        return day !== null && day === this.today();
    }

    /**
     * Parse without shifting the wall clock. A moment is cloned (keeping its own offset); a string
     * goes through `parseZone`. Returns null for absent or unparseable input.
     */
    private static parse(value: CompanyDateInput): moment.Moment | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = moment.isMoment(value)
            ? value.clone()
            : moment.parseZone(value instanceof Date ? moment(value).format() : String(value));
        return parsed.isValid() ? parsed : null;
    }

    /**
     * Backend offsets arrive as TimeSpan strings ("-04:00:00", "05:30:00" — TimeSpan never emits
     * "+") or already-normalized ("+03:00"). Reduce to the ±HH:MM moment accepts.
     *
     * Third copy of this in the repo (`DayRolloverService.normalizeOffset`,
     * `mine.component.resolveUserTzOffset`). Kept public/static so those can converge here rather
     * than a fourth being written; deliberately NOT rewired in this change — `DayRolloverService`
     * is the shipped rollover authority and stays untouched in Phase 1.
     */
    static normalizeOffset(raw: string | undefined): string | null {
        const value = raw ? String(raw).trim() : '';
        if (!value) {
            return null;
        }
        const match = value.match(/^([+-]?)(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (!match) {
            return null;
        }
        const sign = match[1] || '+';
        return `${sign}${match[2].padStart(2, '0')}:${match[3]}`;
    }
}
